/**
 * Value handling for the individual benchmarking bars.
 *
 * The bars are plotted as offsets from the metric's minimum rather than as the
 * real number, so that a bar grows from the bottom of its own scale instead of
 * from the zero line. Everything the reader sees - axis ticks, data labels,
 * tooltips - has to turn that offset back into the real number.
 *
 * A week is asked for as many deliveries as the busiest week in the range has,
 * so most weeks are asked for deliveries they never made. Those have no value
 * and must stay blank; they are not zeroes.
 */

/** Real value -> plotted offset. Missing values stay missing. */
export const toPlotted = (value, min) =>
  value === null || value === undefined ? null : value - min;

/**
 * Plotted offset -> real value. Missing values stay missing: `null + min` is
 * `min` in JavaScript, so without this guard a week with no such delivery
 * reports the bottom of the scale as though it were a measurement.
 */
export const toReal = (plotted, min) =>
  plotted === null || plotted === undefined ? null : plotted + min;

/** Formats a real value for display; missing values render as nothing. */
export const formatValue = (value, decimals) =>
  value === null || value === undefined ? '' : Number(value).toFixed(decimals);

/**
 * Whether the bar at this position stands for something the week actually has.
 *
 * Series 0 is the week average, which every week has. Every later series is one
 * delivery of that week, and a week that made fewer deliveries than the busiest
 * week in the range has nothing to show there.
 */
export const hasBarValue = (week, seriesIndex) => {
  if (!week) {
    return false;
  }
  if (seriesIndex === 0) {
    return Boolean(week.average);
  }
  return Boolean(week.deliveries && week.deliveries[seriesIndex - 1]);
};

/**
 * The y scale of one graph: `min` .. `max` with a tick every `metric.step`,
 * and the `base` its bars grow from.
 *
 * Most scales are fixed and their bars grow from the bottom (`base` = `min`).
 * A metric with `belowZero` (the toeslagen) starts at zero and only drops below
 * it when the period holds a negative value - then to the step at or under the
 * most negative one. Its bars hang from zero, so a negative value points down
 * and the most negative one still has a bar; from the bottom it had none.
 */
export const getScale = (metric, weeks) => {
  let min = metric.min;

  if (metric.belowZero) {
    const values = weeks
      .flatMap(week => [week.average, ...week.deliveries])
      .filter(Boolean)
      .map(row => row[metric.field])
      .filter(value => value !== null && value !== undefined);
    const lowest = Math.min(metric.min, ...values);
    // The tolerance keeps float noise from adding a step: -0.07 / 0.01 is
    // -7.000000000000001 in JavaScript, which would floor to -8.
    const steps = Math.floor(lowest / metric.step + 1e-9);
    min = Number((steps * metric.step).toFixed(10));
  }

  return {
    min,
    max: metric.max,
    tickAmount: Math.round((metric.max - min) / metric.step),
    base: metric.belowZero ? 0 : min,
  };
};

/**
 * Keeps a bar inside the scale. A value past either end - a delivery with no
 * measurements comes through as 0 - would otherwise be drawn below the axis or
 * through the top; it stops at the edge instead and its label still reads the
 * real value (see barValue). Missing values stay missing.
 */
export const clampToScale = (value, scale) =>
  value === null || value === undefined
    ? null
    : Math.min(scale.max, Math.max(scale.min, value));

/**
 * The real value behind a bar: series 0 is the week average, every later series
 * one delivery. Null when the week has no such bar.
 */
export const barValue = (week, seriesIndex, field) => {
  if (!hasBarValue(week, seriesIndex)) {
    return null;
  }
  const row = seriesIndex === 0 ? week.average : week.deliveries[seriesIndex - 1];
  const value = row[field];
  return value === undefined ? null : value;
};
